/**
 * Normalize a note value from a raw amount or row object.
 * @param {number|{amount?: number}|null|undefined} entry
 * @returns {number|null}
 */
export function denominationNoteValue(entry) {
  const note = Number(typeof entry === 'object' && entry !== null ? entry.amount : entry);
  return Number.isFinite(note) ? note : null;
}

/**
 * Unique master notes by rupee value (desc). Prevents repeated rows when
 * ps_rupees or client state accidentally contains duplicates.
 *
 * @param {Array<number|{amount: number}>|null|undefined} masterNotes
 * @returns {number[]}
 */
export function uniqueDenominationNotes(masterNotes) {
  const seen = new Set();
  const notes = [];

  (Array.isArray(masterNotes) ? masterNotes : []).forEach((entry) => {
    const note = denominationNoteValue(entry);
    if (note === null || seen.has(note)) {
      return;
    }
    seen.add(note);
    notes.push(note);
  });

  return notes.sort((a, b) => b - a);
}

/**
 * Align saved denomination rows to the master note list by rupee value.
 * Edit payloads used to be sparse and were bound by array index in the UI,
 * which put counts on the wrong notes. Duplicate note values are collapsed.
 *
 * @param {Array<number|{amount: number}>} masterNotes
 * @param {Array<{amount?: number, count?: number|string, multiple?: number|string}>|null|undefined} savedRows
 * @returns {Array<{amount: number, count: string|number, multiple: string|number}>}
 */
export function alignDenominationsWithMaster(masterNotes, savedRows) {
  const byAmount = new Map();

  (Array.isArray(savedRows) ? savedRows : []).forEach((row) => {
    const note = denominationNoteValue(row);
    if (note === null) {
      return;
    }

    const countNum = Number(row?.count);
    const count = Number.isFinite(countNum) ? countNum : 0;
    const multipleNum = Number(row?.multiple);
    const multiple = Number.isFinite(multipleNum) && row?.multiple !== ''
      ? multipleNum
      : note * count;

    const existing = byAmount.get(note);
    if (existing) {
      existing.count += count;
      existing.multiple += multiple;
      return;
    }

    byAmount.set(note, { amount: note, count, multiple });
  });

  return uniqueDenominationNotes(masterNotes).map((note) => {
    const saved = byAmount.get(note);
    if (!saved || !saved.count) {
      return { amount: note, count: '', multiple: '' };
    }

    return {
      amount: note,
      count: saved.count,
      multiple: saved.multiple,
    };
  });
}

/**
 * Sum denomination lines (rupee * count).
 * @param {Array<{amount?: number, count?: number|string}>|null|undefined} rows
 */
export function sumDenominationAmount(rows) {
  return (Array.isArray(rows) ? rows : []).reduce((sum, row) => {
    const note = Number(row?.amount);
    const count = Number(row?.count);
    if (!Number.isFinite(note) || !Number.isFinite(count) || count === 0) {
      return sum;
    }
    return sum + note * count;
  }, 0);
}

/**
 * Amount field value after denomination counts change (legacy auto-fill).
 * Empty when no counts produce a total so the user can still type Amount.
 * @param {Array<{amount?: number, count?: number|string}>|null|undefined} rows
 * @returns {number|''}
 */
export function amountFromDenominationTotal(rows) {
  const total = sumDenominationAmount(rows);
  return total > 0 ? total : '';
}

/**
 * Apply a count change and return aligned rows plus the auto-filled amount.
 * Shared by Close Payment and Bag Collection so both screens stay in sync.
 *
 * @param {{masterNotes: Array, savedRows: Array, idx: number, countValue: string|number}} params
 * @returns {{aligned: Array, amount: number|''}|null}
 */
export function applyDenominationCountChange({ masterNotes, savedRows, idx, countValue }) {
  const aligned = alignDenominationsWithMaster(masterNotes, savedRows);
  const noteAmount = Number(aligned[idx]?.amount ?? denominationNoteValue(masterNotes?.[idx]));
  if (!Number.isFinite(noteAmount)) {
    return null;
  }

  aligned[idx] = {
    ...aligned[idx],
    amount: noteAmount,
    count: countValue,
    multiple: noteAmount * Number(countValue || 0),
  };

  return {
    aligned,
    amount: amountFromDenominationTotal(aligned),
  };
}

/**
 * Amount to show when opening an existing record.
 * Prefer denomination total; keep saved amount (including paise) when it
 * already matches whole rupees, or when denominations are empty.
 *
 * @param {Array} rows
 * @param {number|string|null|undefined} savedAmount
 */
export function amountOnEditLoad(rows, savedAmount) {
  const total = sumDenominationAmount(rows);
  const hasSaved = savedAmount !== undefined && savedAmount !== null && savedAmount !== '';
  if (total <= 0) {
    return hasSaved ? savedAmount : '';
  }
  if (hasSaved && denominationMatchesAmount(total, savedAmount)) {
    return savedAmount;
  }
  return total;
}

/**
 * Amount to validate or save: typed Amount, otherwise denomination total.
 * @param {number|string|null|undefined} enteredAmount
 * @param {Array|null|undefined} rows
 */
export function resolvedFormAmount(enteredAmount, rows) {
  if (enteredAmount !== undefined && enteredAmount !== null && enteredAmount !== '') {
    return enteredAmount;
  }
  return amountFromDenominationTotal(rows);
}

/**
 * Rounded whole-rupee amount for denomination checks (paise / decimals ignored).
 * @param {number|string|null|undefined} value
 * @returns {number}
 */
export function wholeRupeeAmount(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return 0;
  }
  return Math.round(numeric);
}

/**
 * Paise / decimal remainder of an amount (can be used as round-off display).
 * @param {number|string|null|undefined} value
 * @returns {number}
 */
export function denominationPaiseRoundOff(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return 0;
  }
  return Number((numeric - Math.trunc(numeric)).toFixed(2));
}

/**
 * Denomination total must match the rounded whole-rupee amount (paise ignored).
 * @param {number|string|null|undefined} denominationTotal
 * @param {number|string|null|undefined} amount
 */
export function denominationMatchesAmount(denominationTotal, amount) {
  const denom = Number(denominationTotal);
  if (!Number.isFinite(denom) || denom === 0) {
    return true;
  }
  return wholeRupeeAmount(denom) === wholeRupeeAmount(amount);
}

/**
 * Payload rows to persist: unique notes with count > 0 only.
 * @param {Array<{amount?: number, count?: number|string}>|null|undefined} rows
 */
export function denominationRowsForSave(rows) {
  const aligned = alignDenominationsWithMaster(rows || [], rows || []);
  return aligned.filter((row) => Number(row.count) > 0);
}
